import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "components/ui/dialog";
import VatPaper from "./VatPaper";
import { Setting, VatInvoice } from "lib/models";
import { Button } from "components/ui/button";
import dayjs from "dayjs";
import { cn } from "lib/utils";
import { apiPost } from "components/Common/Axios";
import toast from "react-hot-toast";
import { VatInvoiceCheck } from "./VatApply";

const VatPaperPopup = ({
  open,
  onOpenChange,
  vatInvoices,
  totalVatInvoiceCheck,
  setTotalVatInvoiceCheck,
  setting,
  isApplyMode,
  isReadOnly,
  isShowAddOverlay,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vatInvoices: VatInvoice[];
  totalVatInvoiceCheck: VatInvoiceCheck[];
  setTotalVatInvoiceCheck: (totalVatInvoiceCheck: VatInvoiceCheck[]) => void;
  setting: Setting;
  isApplyMode: boolean;
  isReadOnly: boolean;
  isShowAddOverlay?: boolean;
}) => {
  const [vatInvoice, setVatInvoice] = useState<VatInvoice | undefined>(
    undefined
  );

  useEffect(() => {
    if (vatInvoices.length > 0) {
      setVatInvoice(vatInvoices[0]);
    }
  }, [vatInvoices]);

  const handleExcelDownload = async () => {
    const vatInvoiceIds = vatInvoices.map((invoice) => invoice.id);

    try {
      const response = await apiPost(
        "/vat-invoice/excel",
        vatInvoiceIds,
        false,
        false
      );

      if (response.error) {
        toast.error(response.error);
        console.error("handleApplyVatInvoice error", response.error);
        return;
      }

      // Base64 인코딩된 파일 데이터를 디코딩합니다.
      const fileBase64 = response.data.file;
      const newFileNameFrontend = response.data.newFileNameFrontend;
      const binaryString = window.atob(fileBase64);
      const binaryLen = binaryString.length;
      const bytes = new Uint8Array(binaryLen);

      for (let i = 0; i < binaryLen; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "application/vnd.ms-excel" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", newFileNameFrontend);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);

      // const newFileName = `세금계산서_${vatInvoices[0].supplyContent1 ?? ""}${
      //   vatInvoices.length > 1
      //     ? "_외" + (vatInvoices.length - 1).toString()
      //     : ""
      // }.xls`;

      // const blob = new Blob([response], {
      //   type: "application/vnd.ms-excel",
      // });
      // const url = window.URL.createObjectURL(blob);
      // const link = document.createElement("a");
      // link.href = url;
      // link.setAttribute("download", newFileName);
      // document.body.appendChild(link);
      // link.click();
      // link.remove();
      // setTimeout(() => {
      //   window.URL.revokeObjectURL(url);
      // }, 100);
    } catch (error: any) {
      console.error("handleExcelDownload Error", error.message);
    }
  };

  const handleApplyVatInvoice = async (isIssuedCompleted: boolean) => {
    const vatInvoiceIds = vatInvoices.map((invoice) => invoice.id);

    if (isIssuedCompleted) {
      // 발급
      const response = await apiPost("/vat-invoice/apply", {
        vatInvoiceIds,
      });

      if (response.error) {
        toast.error("세금계산서 발급상태 변경에 실패했습니다.");
        console.error("handleApplyVatInvoice error", response.error);
        return;
      }

      toast.success("세금계산서 발급상태로 변경되었습니다.");

      setTotalVatInvoiceCheck(
        totalVatInvoiceCheck.map((vatInvoiceCheck) =>
          vatInvoiceIds.includes(vatInvoiceCheck.vatInvoice.id)
            ? {
                ...vatInvoiceCheck,
                vatInvoice: {
                  ...vatInvoiceCheck.vatInvoice,
                  isIssuedCompleted,
                },
              }
            : vatInvoiceCheck
        )
      );
    } else {
      // 발급취소
      const response = await apiPost("/vat-invoice/cancel", {
        vatInvoiceIds,
      });

      if (response.error) {
        toast.error("세금계산서 발급상태 변경에 실패했습니다.");
        console.error("handleApplyVatInvoice error", response.error);
        return;
      }

      toast.success("세금계산서 발급이 취소되었습니다.");

      setTotalVatInvoiceCheck(
        totalVatInvoiceCheck.filter(
          (vatInvoiceCheck) =>
            !vatInvoiceIds.includes(vatInvoiceCheck.vatInvoice.id)
        )
      );
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-lg max-h-[95%] overflow-x-hidden overflow-y-auto thin-scrollbar">
        <DialogHeader></DialogHeader>
        <div className="text-center text-xl sm:text-2xl font-bold text-gray-900">
          세금계산서 발급{" "}
          <span className="text-base">(총 {vatInvoices.length}건)</span>
        </div>
        <div className="text-xs border-solid border-b border-t border-gray-500 b-4 overflow-x-auto"><div className="min-w-[900px]">
          <div className="grid grid-cols-[50px_160px_100px_100px_90px_80px_80px_100px_1fr] border-solid border-b border-t border-gray-200 py-2 bg-gray-50 text-gray-500 font-medium pl-4">
            <div className="text-center">발급</div>
            <div>학교명</div>
            <div>거래처명</div>
            <div>발급처명</div>
            <div className="text-right">총액</div>
            <div className="text-right">공급가액</div>
            <div className="text-right">세액</div>
            <div className="text-center">발급일</div>
            <div className="">내용</div>
          </div>
          {vatInvoices.map((invoice: VatInvoice, index: number) => (
            <div
              key={invoice.id}
              onClick={(e) => {
                setVatInvoice(invoice);
              }}
              className={cn(
                "grid grid-cols-[50px_160px_100px_100px_90px_80px_80px_100px_1fr] text-xs border-solid border-b border-gray-200 pl-4 py-2 items-center cursor-pointer",
                invoice.id === vatInvoice?.id ? "bg-yellow-100" : "bg-white"
              )}
            >
              <div>
                {invoice.isIssuedCompleted && (
                  <div className="bg-cyan-500 text-xs rounded-md w-10 h-5 text-center text-white pt-[2px]">
                    발급
                  </div>
                )}
              </div>
              <div className="whitespace-pre-wrap">
                {Array.from(
                  new Set(
                    invoice.schoolSalesMaterial
                      .map((sales) => sales.school?.name ?? "")
                      .concat(
                        invoice.schoolSales.map(
                          (sales) => sales.school?.name ?? ""
                        )
                      )
                  )
                ).join("\n")}
              </div>
              <div>{invoice.vatCompany?.nickName}</div>

              <div>{invoice.vatCompany?.name}</div>
              <div className="text-right">
                {invoice.totalAmt.toLocaleString()}
              </div>
              <div className="text-right">
                {invoice.supplyAmt.toLocaleString()}
              </div>
              <div className="text-right">
                {invoice.taxAmt.toLocaleString()}
              </div>
              <div className="text-center">
                {dayjs(invoice.issuedDate).toDate().toLocaleDateString()}
              </div>
              <div className="whitespace-pre-wrap">
                {[
                  invoice.supplyContent1,
                  invoice.supplyContent2,
                  invoice.supplyContent3,
                  invoice.supplyContent4,
                ]
                  .filter((content) => content !== "")
                  .join(", ")}
              </div>
            </div>
          ))}
        </div>
        </div>

        {vatInvoice && (
          <>
            <VatPaper vatInvoice={vatInvoice} setting={setting} />
            {!isReadOnly && (
              <div className="flex flex-wrap justify-between items-center gap-3 px-2 md:px-10">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => handleExcelDownload()}
                >
                  엑셀다운로드
                </Button>
                <div className="flex gap-2">
                  {isApplyMode ? (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => handleApplyVatInvoice(true)}
                    >
                      발급상태로 변경
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => handleApplyVatInvoice(false)}
                    >
                      발급상태 취소
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => onOpenChange(false)}
                  >
                    닫기
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VatPaperPopup;
